// exported for testing

export const name = 'rnRegistry';
export default function rnRegistryProvider() {

  let apiVersions = {
    aaa : 4,
    system_statistics : 2
  };

  this.setApiVersions = setApiVersions;
  this.$get = rnRegistry;

  return;

  function setApiVersions(versions = {}) {
    apiVersions = versions;
  }

  /**
   * @ngdoc service
   * @name rhino.core.registry.service:rnRegistry
   * @description
   * `rnRegistry` is a service that retrieves and caches service registry information from Genesis servers.
   */
  function rnRegistry($log, $http, $q, $window, rnBlue, rnClient) {
    //noinspection BadExpressionStatementJS
    'ngInject';

    var service = {
      getKey: getKey,
      getUrl: getUrl,
      getImageUrl: getImageUrl,
      getServiceName: getServiceName,
      getPath: getPath,
      getRegistry: getRegistry,
      pusherAuthEndpoint: pusherAuthEndpoint
    };

    const coreModule = 'rhino.core';
    const servicesName = 'rnServices';
    const keysName = 'rnKeys';

    var cached_registry;
    var nbh = true;   // never been here

    return service;

    ////////////////

    /**
     * @ngdoc function
     * @name getKey
     * @methodOf rhino.core.registry.service:rnRegistry
     * @param {string} keyName Name of the service registry key to retrieve
     *
     * @description
     * Return a specific keys value from the registry as a promise.
     */
    function getKey(keyName) {
      return getRegistry().then(function (data) {
        if (data.keys[keyName] === undefined) {
          $log.warn('No services key for ' + keyName);
          return $q.reject('No services key for ' + keyName);
        }
        return data.keys[keyName];
      });
    } //getKey


    /**
     * @ngdoc function
     * @name getPath
     * @methodOf rhino.core.registry.service:rnRegistry
     * @param {string} serviceName Name of the service to retrieve the absolute path for.
     * @param {string} path Relative path to be appended to service path.
     *
     * @description
     * Return a URL absolute path without the authority (protocol, server, or port) based on
     * the service and the relative path requested as a promise.
     *
     *```js
     * rnRegistry.getPath('AAA', 'users')
     *   .then(function(path) {
   *     // promise resolves to a path here.
   *     // For example: "/api/aaa/v1/users"
   *   });
     *```
     */
    function getPath(serviceName, path = '') {
      $log.info(`rnRegistry#getPath(${serviceName}, ${path}) called. Consider calling getUrl instead.`);
      return getRegistry().then(function (data) {
        if (data.services[serviceName] === undefined) {
          $log.warn('No service registry entry for service:' + serviceName);
          return $q.reject('No service registry entry for service:' + serviceName);
        }
        var base = data.services[serviceName].proxy;
        if ((path.length > 0) && (base.charAt(base.length - 1) !== '/') && (path.charAt(0) !== '/')) {
          return base + '/' + path;
        } else {
          return base + path;
        }
      });
    } //getPath


    /**
     * @ngdoc function
     * @name getUrl
     * @methodOf rhino.core.registry.service:rnRegistry
     * @param {string} serviceName Name of the service to retrieve the absolute path for.
     * @param {string} [path=''] Relative path to be appended to service path.
     * @param {boolean} [appendTrailingSlash=true] true to ensure the service base and path are joined with a slash
     * @param {number|undefined} [version] API version number to formulate URL with
     *
     * @description
     * Return a full URL with any authority information (protocol, server, port) based on
     * the service and the relative path requested as a promise.
     *
     *```js
     * rnRegistry.getUrl('portal_service', 'apps')
     *   .then(function(url) {
   *     // promise resolves to a url here.
   *     // For example: "https://daily.gsbprint.net/api/portal/v1/apps"
   *   });
     *```
     */
    function getUrl(serviceName, path = '', appendTrailingSlash = true, version = undefined) {
      return getRegistry().then(function (data) {
        if (data.services[serviceName] === undefined) {
          $log.warn('No service registry entry for ' + serviceName);
          return $q.reject('No service registry entry for ' + serviceName);
        }

        let base = getServiceBaseUrl(data.services[serviceName], serviceName, version);
        if (appendTrailingSlash) {
          return joinUrlParts(base, path);
        } else {
          return base + path;
        }
      });
    } //getUrl

    /**
     * Gets the base URL of a service, formed with a version if provided.
     *
     * @param {object} service service registry data for service
     * @param {string} serviceName name of the service to form the URL for
     * @param {number} [version] API version to form URL with
     */
    function getServiceBaseUrl(service, serviceName, version) {
      let baseUrl = service.base_sv_url;
      if (!baseUrl) {
        if (version !== undefined && version > 1) {
          $log.warn('API version (' + version + ') specified is >1 for ' + serviceName + ' that does not support versioning. Returning default sv_url');
        }
        // unversioned service - simply return regular service url
        return service.sv_url;
      }

      if (version === undefined) {
        // Use configured version if present
        if(apiVersions[serviceName]) {
          version = apiVersions[serviceName];
        } else {
          // Fallback to version 1 (keeps backwards comparability and generally safe)
          version = 1;
        }
      }
      return joinUrlParts(baseUrl, 'v' + version);
    }

    function joinUrlParts(a, b) {
      if (b.charAt(0) === '/') {
        // Prevent double slashes by ensuring part b doesn't have a slash
        b = b.substr(1);
      }

      if (a.charAt(a.length - 1) === '/') {
        return a + b;
      }
      return a + '/' + b;
    }

    /**
     * @ngdoc function
     * @name getServiceName
     * @methodOf rhino.core.registry.service:rnRegistry
     * @param {string} url requested URL to determine service name from
     * @returns {Promise<string|undefined>} the service name or undefined if not found
     *
     * @description
     * Determines the service name from a URL (the inverse of getUrl()).
     *
     *```js
     * rnRegistry.getServiceName('https://daily.gsbprint.net/api/aaa/organizations/devices')
     *   .then(serviceName => {
     *     expect(serviceName).toBe('aaa');
     *   });
     *```
     */
    function getServiceName(url) {
      return getRegistry().then(data => {
        return _.findKey(data.services, service => {
          let serviceUrl = service.base_sv_url;
          if(!serviceUrl) {
            serviceUrl = service.sv_url;
          }
          if(!serviceUrl) {
            return false;
          }
          return _.includes(url, serviceUrl);
        });

      });
    }

    /**
     * @ngdoc function
     * @name getImageUrl
     * @methodOf rhino.core.registry.service:rnRegistry
     * @param {string} [path=''] relative path of the image.
     * @param {string} [appendTrailingSlash=true] true to ensure the service base and path are joined with a slash
     *
     * @description
     * Returns a full URL to a PrintOS common image.
     *
     *```js
     * rnRegistry.getImageUrl('devices/Indigo_1000.png')
     *   .then(function(imgUrl) {
   *     // promise resolves to a url here.
   *     // For example: "https://daily.gsbprint.net/images/devices/Indigo_1000.png"
   *   });
     *```
     */
    function getImageUrl(path = '', appendTrailingSlash = true) {
      return getUrl('printos_images', path, appendTrailingSlash);
    }


    function getRegistry() {
      var rDeferred = $q.defer();
      // set the content type and cached values in the case this is called during angular deferred bootstrap
      rnClient.getConfig().then(
        function (clientConfig) {
          if (nbh) {
            nbh = !nbh;
            $log.debug('rnRegistry.getRegistry: ', clientConfig);
          }
          let registry = clientConfig.registry;

          // Would like to import module name but not create circular dependency
          var rhinoCore = angular.module(coreModule);
          rhinoCore.constant(servicesName, registry.services);
          rhinoCore.constant(keysName, registry.keys);
          cached_registry = registry;
          rDeferred.resolve(registry);
        },
        function (response) {
          rDeferred.reject(response.statusText);
          $log.error(`rnRegistry.getRegistry error: ${JSON.stringify(response)}  status: ${response.status}  config: ${JSON.stringify(response.config)}`);
        });
      return rDeferred.promise;
    } //getRegistry

    function pusherAuthEndpoint() {
      if (cached_registry) {
        return cached_registry.services.aaa.base_sv_url + 'v3/client-eventing/authorize-subscription-request';
      } else {
        $log.error('called pusherAuthEndpoint when registry wasn\'t cached!');
      }
    }
  } //rnRegistry
} //rnRegistryProvider
